import { Injector } from '@angular/core';
import { draggableNodeStopEvent } from '@examdojo/core/tiptap';
import { findParentNode, mergeAttributes, Node } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import { AngularNodeViewRenderer } from 'ngx-tiptap';
import { DraggableItemComponent } from './draggable-item.component';

export const DRAGGABLE_ITEM_NODE_NAME = 'draggableItem';
const draggableItemClassName = 'draggable-item';

export const DraggableItemExtension = (injector: Injector) => {
  return Node.create({
    name: DRAGGABLE_ITEM_NODE_NAME,
    group: 'block',
    content: 'block',
    draggable: true,

    parseHTML() {
      return [
        {
          tag: `div[data-type="${draggableItemClassName}"]`,
        },
      ];
    },

    renderHTML({ HTMLAttributes }) {
      return ['div', mergeAttributes(HTMLAttributes, { 'data-type': draggableItemClassName }), 0];
    },

    addNodeView() {
      return AngularNodeViewRenderer(DraggableItemComponent, {
        injector,
        stopEvent: draggableNodeStopEvent,
      });
    },

    addKeyboardShortcuts() {
      return {
        Enter: ({ editor }) => {
          const { selection, doc } = editor.state;
          const { $from } = selection;

          const draggableItem = findParentNode((node) => node.type.name === DRAGGABLE_ITEM_NODE_NAME)(selection);
          if (!draggableItem) {
            return false;
          }

          // Check if draggableItem is inside a container (not directly under doc)
          const draggableItemParent = doc.resolve(draggableItem.pos).parent;
          if (draggableItemParent.type.name === 'doc') {
            return false;
          }

          // Split at depth 2: splits both the inner block AND the draggableItem wrapper
          return editor
            .chain()
            .command(({ tr }) => {
              tr.split($from.pos, 2);
              return true;
            })
            .run();
        },
        Backspace: ({ editor }) => {
          const { selection, doc } = editor.state;
          const { $from } = selection;

          const draggableItem = findParentNode((node) => node.type.name === DRAGGABLE_ITEM_NODE_NAME)(selection);
          if (!draggableItem || draggableItem.node.textContent.trim() !== '' || $from.parentOffset !== 0) {
            return false;
          }

          const { node, pos } = draggableItem;
          const $pos = doc.resolve(pos);
          const parent = $pos.parent;
          const deleteContainer = parent.type.name !== 'doc' && parent.childCount === 1;

          const from = deleteContainer ? $pos.before($pos.depth) : pos;
          const to = deleteContainer ? from + parent.nodeSize : pos + node.nodeSize;

          return editor
            .chain()
            .command(({ tr, dispatch }) => {
              if (dispatch) {
                tr.delete(from, to);
                tr.setSelection(TextSelection.near(tr.doc.resolve(Math.max(0, from - 1)), -1));
              }
              return true;
            })
            .run();
        },
      };
    },
  });
};
